/**
 * Component: Recipe Swiper with Container + View Pattern + Phase 3 Pre-Seeding
 *
 * This is a component-level file that delegates to the centralized
 * Container + View pattern for clean separation of concerns.
 *
 * Phase 3 Enhancement: Intelligently manages recipe pre-seeding based on user state:
 * - New users (just completed TempTaste): Pre-seed with vector-matched recipes
 * - Returning users: Load from meal plan history or start fresh
 * - Cold deck: Fetch initial batch on demand
 */

import { useState, useEffect, useRef } from "react";
import { useAction } from "wasp/client/operations";
import { getInitialRecipeRecommendations } from "wasp/client/operations";
import RecipeSwiperContainer from "./RecipeSwiper.container";
import type { Recipe } from "./RecipeSwiper.container";
import type { V32UITier } from '../../types/subscription';

interface RecipeSwiperProps {
  recipes: Recipe[];
  onSwipeRight: (recipe: Recipe) => void;
  onSwipeLeft: (recipe: Recipe) => void;
  onSwipeUp: (recipe: Recipe) => void;
  onSwipeDown: (recipe: Recipe) => void;
  onComplete: (selectedRecipes: Recipe[], savedRecipes: Recipe[]) => void;
  onBack: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  showProgress?: boolean;
  enableHaptics?: boolean;
  currentTier?: V32UITier;
  onUpgrade?: () => void;
  // Add missing fields for Recipe type
  createdAt?: Date;
  updatedAt?: Date;
  servings?: number;
  slug?: string;
}

export function RecipeSwiper(props: RecipeSwiperProps) {
  const [preSeededRecipes, setPreSeededRecipes] = useState<any[] | null>(null);
  const [isPreSeeding, setIsPreSeeding] = useState(false);
  const getRecipes = useAction(getInitialRecipeRecommendations);
  const hasPreseededRef = useRef(false);

  // Phase 3: Detect if user just completed TempTaste and pre-seed if needed
  useEffect(() => {
    const shouldPreSeed =
      sessionStorage.getItem("temptaste_just_completed") === "true";

    if (shouldPreSeed && !hasPreseededRef.current) {
      hasPreseededRef.current = true;
      setIsPreSeeding(true);

      (async () => {
        try {
          // Get initial recommendations with empty formData to use defaults
          const recipes = await getRecipes({ formData: {} });
          setPreSeededRecipes(recipes);

          // Clear flag after using it to prevent re-seeding on refresh
          sessionStorage.removeItem("temptaste_just_completed");
        } catch (error) {
          console.error("Failed to pre-seed recipes:", error);
          setPreSeededRecipes([]);
        } finally {
          setIsPreSeeding(false);
        }
      })();
    }
  }, [getRecipes]);

  // Delegate to container with optional pre-seeded recipes
  return (
    <RecipeSwiperContainer
      {...props}
      preSeededRecipes={preSeededRecipes || undefined}
      shouldPreSeed={true}
    />
  );
}

// Re-export types for backward compatibility  
export type { Recipe };
